import { waitForItPromise } from 'src/lib/utils';
import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import { CollideEvent, CollideId, isPowerCollider } from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BalloonBoxBlockEntity } from './BalloonBoxBlockEntity';
import {
  BalloonBoxStateFlag,
  balloonBoxColors,
  balloonBoxOptionCheckStateComplete,
} from './BalloonBoxBlock';
import { BalloonBoxBlockEffect } from './BalloonBoxBlockEffect';
import { app } from '../../app';
import { RadialAttackEffect } from '../../actions/effects/RadialAttackEffect';
import { ColorId, PowerBlockType } from '../../defs/block';
import { randomShuffle } from '../../../odie';

export class BalloonBoxBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: BalloonBoxBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: BalloonBoxBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    await this.blockHit(event);
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    await this.powerHit(event);
    return true;
  }

  private async blockHit(event: CollideEvent) {
    if (event.collider?.type !== 'block') return;
    const block = event.collider?.entity;
    if (!block) return;
    const type = block.c.block.props.type;
    if (type !== 'basic') return;
    const color = block.c.block.props.color;
    await this.hit(color);
  }

  private async powerHit(event: CollideEvent) {
    const type = event.collider?.type as PowerBlockType;
    if (!isPowerCollider(type)) return;
    const state = this.entity.c.blockBalloonBox.getState();
    // Power hit should trigger a random balloon color
    const shuffledColorIds = randomShuffle(balloonBoxColors.slice());
    for (const color of shuffledColorIds) {
      // Check if the balloon box has a balloon of this color
      const balloonA = `${color}A` as BalloonBoxStateFlag;
      const balloonB = `${color}B` as BalloonBoxStateFlag;
      if (!state[balloonA] || !state[balloonB]) {
        await this.hit(color);
        return;
      }
    }
  }

  private async hit(color: ColorId) {
    const component = this.entity.c.blockBalloonBox;
    const filledUp = component.fillBalloon(color);
    if (filledUp) app.sound.play('puzzle-lock-destroy');

    // Check if the balloon box is complete and despawn it if it is
    const state = this.entity.c.blockBalloonBox.getState();
    if (balloonBoxOptionCheckStateComplete(state)) {
      const effects = new BalloonBoxBlockEffect(this.scene);
      component.explode();
      app.sound.play('puzzle-combo-cube-replace');
      effects.playExplodeAnimation(this.entity);
      await waitForItPromise(1700);
      this.executeExplosion();
      effects.playBreakParticles(this.entity);
      await waitForItPromise(300);
      despawnBlockEntity(this.scene, this.entity);
    }
  }

  private async executeExplosion() {
    const grid = this.scene.mapDef?.grid;
    const dist = grid ? Math.max(grid.columns, grid.rows) : 9;
    const radius = Math.ceil(Math.sqrt(dist * dist + dist * dist));
    const position = this.entity.c.position.mapPosition.clone();

    // This block is 3x3 - the origin should be the center of the block
    position.x += 1;
    position.y += 1;

    // Attack all blocks in the puzzle, like lollipop combo
    const effect = new RadialAttackEffect(this.scene, {
      position,
      radius,
      duration: 0.6,
      // Cube collisions makes destroyed blocks trigger surrounding collisions, like regular tap
      // but the big lollipop should behave more like a bomb, hitting all existing blocks only once
      collider: { type: 'cubeAll' },
    });
    await effect.execute();
  }
}
